// utils/clientStats.ts

import { Client, ClientStatus } from "@/types/types";
import { isSubscriptionExpired } from "@/components/subscriptions/SubscriptionFormSchema";

export function clientDataToClient(clientData: any): Client {
  return {
    id: clientData.id,
    firstName: clientData.firstName,
    lastName: clientData.lastName,
    email: clientData.email || "",
    phone: clientData.phone || "",
    membershipType: clientData.membershipType || "Standard",
    status: (clientData.status as ClientStatus) || "Active",
    startDate: clientData.startDate || clientData.joinDate || "",
    expiryDate: clientData.expiryDate || "",
    lastVisit: clientData.lastVisit || "",
    address: clientData.address || "",
    birthDate: clientData.birthDate || "",
    emergencyContact: clientData.emergencyContact || "",
    notes: clientData.notes || "",
    clothingSize: clientData.clothingSize || "M",
    bankInformation: clientData.bankInformation || "",
    sessionsRemaining: clientData.sessionsRemaining ?? 0,
    jokersRemaining: clientData.jokersRemaining ?? 0,
    sessionsTotal: clientData.sessionsTotal ?? 0,
    jokersTotal: clientData.jokersTotal ?? 0,
    subscriptionHistory: (clientData.subscriptionHistory as any[]) || [],
    profilePhotoUrl: clientData.profilePhotoUrl,
    breakDateStart: clientData.breakDateStart,
    breakDateEnd: clientData.breakDateEnd,
    lastActivity: clientData.lastActivity,
    createdAt: clientData.createdAt,
    gymId: clientData.gymId,
    lastBookingAt: clientData.lastBookingAt ?? null,
  };
}

export function prepareClientList(rawClients: any[]): Client[] {
  return rawClients.map((raw) => {
    const c = clientDataToClient(raw);

    const isDateExpired = !!c.expiryDate && isSubscriptionExpired(c.expiryDate) && c.status !== "Blocked";
    const isSessionsDepleted = (c.sessionsTotal ?? 0) > 0 && (c.sessionsRemaining ?? 0) <= 0;

    if ((isDateExpired || isSessionsDepleted) && c.status !== "Paused") {
      return { ...c, status: "Expired" as ClientStatus };
    }

    return c;
  });
}

// Helpers
const isExpiringSoon = (expiryDate?: string) => {
  if (!expiryDate) return false;
  const today = new Date();
  const expiry = new Date(expiryDate);
  const diff = (expiry.getTime() - today.getTime()) / (1000 * 3600 * 24);
  return diff > 0 && diff <= 14;
};

type ClientLite = {
  id: string;
  status: string;
  expiryDate?: string | Date | null;
  sessionsTotal?: number | null;
  sessionsRemaining?: number | null;
  firstName?: string;
  lastName?: string;
  email?: string;
};

function toISO(d?: string | Date | null) {
  if (!d) return null;
  try {
    const dt = typeof d === "string" ? new Date(d) : d;
    if (isNaN(dt.getTime())) return null;
    return dt.toISOString().slice(0, 10); // YYYY-MM-DD
  } catch {
    return null;
  }
}

export function reconcileClients(
  clients: ClientLite[],
  opts: {
    isDateExpired: (c: ClientLite) => boolean;
    isSessionsDepleted: (c: ClientLite) => boolean;
    isExpiringSoon: (d?: string | Date | null) => boolean;
  }
) {
  const { isDateExpired, isSessionsDepleted, isExpiringSoon } = opts;

  const inActive = (c: ClientLite) =>
    !isDateExpired(c) &&
    !isSessionsDepleted(c) &&
    c.status !== "Blocked" &&
    c.status !== "Inactive";

  const inExpired = (c: ClientLite) =>
    (c.status === "Expired" || isDateExpired(c) || isSessionsDepleted(c)) &&
    c.status !== "Paused";

  const activeArr = clients.filter(inActive);
  const expiredArr = clients.filter(inExpired);

  const activeIds = new Set(activeArr.map((c) => c.id));
  const expiredIds = new Set(expiredArr.map((c) => c.id));

  const missing = clients.filter((c) => !activeIds.has(c.id) && !expiredIds.has(c.id));

  // Détails pour comprendre
  const details = missing.map((c) => {
    const expiredByDate = isDateExpired(c);
    const depleted = isSessionsDepleted(c);
    const soon = isExpiringSoon(c.expiryDate);
    const iso = toISO(c.expiryDate);

    // Raison principale
    let reason = "";
    if (c.status === "Paused") reason = "Paused (exclu d'expired, potentiellement non actif selon règles)";
    else if (c.status === "Blocked") reason = "Blocked (exclu d'active; pas expired si pas de date/sessions expirés)";
    else if (c.status === "Inactive") reason = "Inactive (exclu d'active; pas expired si pas de date/sessions expirés)";
    else if (!expiredByDate && !depleted) reason = "Pas expired & pas depleted mais exclu d'active par le statut";
    else reason = "Cas particulier / data";

    return {
      id: c.id,
      name: [c.firstName, c.lastName].filter(Boolean).join(" ") || undefined,
      email: c.email,
      status: c.status,
      expiryISO: iso,
      sessions: `${c.sessionsRemaining ?? 0}/${c.sessionsTotal ?? 0}`,
      flags: {
        expiredByDate,
        depleted,
        expiringSoon: soon,
        blocked: c.status === "Blocked",
        inactive: c.status === "Inactive",
        paused: c.status === "Paused",
      },
      reason,
    };
  });

  return {
    counts: {
      total: clients.length,
      active: activeArr.length,
      expired: expiredArr.length,
      missing: missing.length,
    },
    activeIds,
    expiredIds,
    missingIds: missing.map((c) => c.id),
    missingDetails: details,
  };
}



export function computeClientKpis(clients: Client[]) {
  const isDateExpired = (c: Client) => !!c.expiryDate && isSubscriptionExpired(c.expiryDate);
  const isSessionsDepleted = (c: Client) => (c.sessionsTotal ?? 0) > 0 && (c.sessionsRemaining ?? 0) <= 0;

  const active = clients.filter(c =>
    c.status !== "Expired" &&
    c.status !== "Blocked" &&
    c.status !== "Inactive"
  ).length;

  const expSoon = clients.filter(c =>
    (
      (isExpiringSoon(c.expiryDate) && !["Blocked", "Inactive"].includes(c.status) && !isDateExpired(c)) ||
      ((c.sessionsTotal ?? 0) > 0 && (c.sessionsRemaining ?? 0) <= 2 && !isSessionsDepleted(c))
    ) && c.status !== "Paused"
  ).length;

  const expired = clients.filter(c =>
    (c.status === "Expired" || isDateExpired(c) || isSessionsDepleted(c)) && c.status !== "Paused"
  ).length;

  const expiredWithSessions = clients.filter(c =>
    (c.status === "Expired" || isDateExpired(c)) && c.status !== "Paused" &&
    (c.sessionsTotal ?? 0) > 0 &&
    (c.sessionsRemaining ?? 0) > 0
  ).length;

  const expiredNoSessions = clients.filter(c =>
    (c.status === "Expired" || isDateExpired(c)) && c.status !== "Paused" &&
    (c.sessionsTotal ?? 0) > 0 &&
    (c.sessionsRemaining ?? 0) === 0
  ).length;

  const inactive = clients.filter(c => c.status === "Inactive").length;
  const blocked = clients.filter(c => c.status === "Blocked").length;
  const pausedClients = clients.filter(c => c.status === "Paused");
  const totalPaused = pausedClients.length;

//   const diag = reconcileClients(clients, { isDateExpired, isSessionsDepleted, isExpiringSoon });
// console.log("Reconcile:", diag.counts);         // => { total: 155, active: 139, expired: 8, missing: 8 } (exemple)
// console.table(diag.missingDetails);            // => liste des 8 comptes avec raisons

  return {
    active,
    expSoon,
    expired,
    expiredWithSessions,
    expiredNoSessions,
    inactive,
    blocked,
    pausedClients,
    totalPaused,
    total: clients.length,
  };
}
